import { useEffect, useId, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  archiveClient,
  archiveSite,
  createClient,
  createSite,
  fetchClientSiteSetup,
  SAFE_CLIENT_SITE_WRITE_FAILURE,
  setSiteLifecycleStatus,
  updateClient,
  updateSite,
  type ClientRow,
  type ClientSiteWriteOutcome,
  type ManagedSite,
  type SiteDraft,
  type ClientSiteSetup,
} from "../data/clients-sites";
import { useReadModel } from "../data/useReadModel";
import { confirmDiscardIfDirty } from "../components/confirm-discard";
import { GlassDrawer, GlassDrawerFooter } from "../components/GlassDrawer";
import { GlassModal } from "../components/GlassModal";
import { TimezoneSelect } from "../components/TimezoneSelect";
import { DEFAULT_SITE_TIMEZONE } from "../lib/timezone-options";
import {
  formatGeocodeQuery,
  geocodeAddress,
  normalizeSiteAddressFields,
  resolveSiteDraftWithCenter,
  SITE_ADDRESS_REQUIRED_MESSAGE,
  siteDraftHasRequiredAddress,
} from "../lib/geocode";

// Client/site editor (CONSOLE-SETUP-CLIENT-SITE-EDITOR). The top of the property chain:
// clients (property groups) with their sites nested, archived ones visible and labeled.
// Reads come only from the hosted setup view (site-less clients included); writes go ONLY
// through the six hosted RPCs; archive-over-delete with consequence copy; viewers without
// schedule.manage get the read-only grouped list. With this, a whole new property stands
// up from the console: client -> site -> route -> checkpoints -> tokens -> schedule.

type Dialog =
  | { kind: "create-client" }
  | { kind: "edit-client"; row: ClientRow }
  | { kind: "archive-client"; row: ClientRow }
  | { kind: "create-site"; clientRow: ClientRow }
  | { kind: "edit-site"; site: ManagedSite }
  | { kind: "archive-site"; site: ManagedSite }
  | { kind: "lifecycle-site"; site: ManagedSite; next: "live" | "setup" };

function isDrawerDialog(dialog: Dialog): boolean {
  return (
    dialog.kind === "create-client" ||
    dialog.kind === "edit-client" ||
    dialog.kind === "create-site" ||
    dialog.kind === "edit-site"
  );
}

function dialogAriaLabel(dialog: Dialog): string {
  switch (dialog.kind) {
    case "create-client":
      return "New client";
    case "edit-client":
      return `Edit client - ${dialog.row.name}`;
    case "create-site":
      return `New site under ${dialog.clientRow.name}`;
    case "edit-site":
      return `Edit site - ${dialog.site.name}`;
    case "archive-client":
      return `Archive client - ${dialog.row.name}`;
    case "archive-site":
      return `Archive site - ${dialog.site.name}`;
    case "lifecycle-site":
      return `${dialog.next === "live" ? "Take live" : "Return to setup"} - ${dialog.site.name}`;
  }
}

function focusedClientId(dialog: Dialog | null): string | null {
  if (dialog === null) return null;
  if (dialog.kind === "edit-client" || dialog.kind === "archive-client") return dialog.row.clientId;
  if (dialog.kind === "create-site") return dialog.clientRow.clientId;
  return null;
}

function focusedSiteId(dialog: Dialog | null): string | null {
  if (dialog === null) return null;
  if (
    dialog.kind === "edit-site" ||
    dialog.kind === "archive-site" ||
    dialog.kind === "lifecycle-site"
  ) {
    return dialog.site.siteId;
  }
  return null;
}

export function ClientSiteEditor({
  client,
  accountId,
  canManage = false,
  mapTilerKey,
  onSetupChanged,
}: {
  client: SupabaseClient;
  accountId: string;
  /** schedule.manage - write affordances only exist when true (fail closed). */
  canManage?: boolean;
  mapTilerKey?: string | undefined;
  /** Notify parent Setup sections to refresh site/client pickers (routes, contacts). */
  onSetupChanged?: () => void;
}) {
  const { status, data, error, reload } = useReadModel<ClientSiteSetup>(
    () => fetchClientSiteSetup(client, accountId),
    [client, accountId],
  );
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [formDirty, setFormDirty] = useState(false);

  const drawerDialogKey =
    dialog !== null && isDrawerDialog(dialog)
      ? dialog.kind === "edit-client"
        ? `edit-client:${dialog.row.clientId}`
        : dialog.kind === "edit-site"
          ? `edit-site:${dialog.site.siteId}`
          : dialog.kind === "create-site"
            ? `create-site:${dialog.clientRow.clientId}`
            : dialog.kind
      : null;

  useEffect(() => {
    setFormDirty(false);
  }, [drawerDialogKey]);

  function clearDrawer(): void {
    setDialog(null);
    setFailure(null);
    setFormDirty(false);
  }

  function requestDrawerClose(): void {
    if (busy) return;
    if (!confirmDiscardIfDirty(formDirty)) return;
    clearDrawer();
  }

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading clients and sites...</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load clients and sites."}
      </p>
    );
  }

  const { clients, sites } = data ?? { clients: [], sites: [] };
  const activeClientId = focusedClientId(dialog);
  const activeSiteId = focusedSiteId(dialog);

  async function run(
    action: () => Promise<ClientSiteWriteOutcome>,
    onSuccess?: (outcome: Extract<ClientSiteWriteOutcome, { ok: true }>) => boolean | void,
  ): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const outcome = await action();
      if (outcome.ok) {
        const keepOpen = onSuccess?.(outcome) === true;
        if (!keepOpen) setDialog(null);
        reload();
        onSetupChanged?.();
      } else {
        setFailure((outcome as { reason: string }).reason);
      }
    } catch {
      setFailure(SAFE_CLIENT_SITE_WRITE_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="csedit">
      {canManage ? (
        <div className="tpl__actions">
          <button
            type="button"
            className="minter__confirm-go"
            onClick={() => {
              setFailure(null);
              setDialog({ kind: "create-client" });
            }}
          >
            New client
          </button>
        </div>
      ) : null}
      {clients.length === 0 ? (
        <p className="surface__state" role="status">
          No clients yet{canManage ? " - create the first one above" : ""}. Clients group the sites
          you protect.
        </p>
      ) : (
        clients.map((row) => {
          const clientSites = sites.filter((site) => site.clientId === row.clientId);
          return (
            <section
              key={row.clientId}
              className={`ckedit__group${activeClientId === row.clientId ? " ckedit__group--editing" : ""}`}
              role="region"
              aria-label={`${row.name} sites`}
            >
              <div className="ckedit__head">
                <span className="ckedit__route">{row.name}</span>
                <span className="schedule__chip schedule__chip--partial">
                  {clientSites.length === 1 ? "1 site" : `${clientSites.length} sites`}
                </span>
                {row.active ? null : <span className="schedule__chip schedule__chip--draft">Archived</span>}
                {canManage && row.active ? (
                  <span className="ckedit__actions">
                    <button
                      type="button"
                      className="minter__confirm-go ckedit__add"
                      onClick={() => {
                        setFailure(null);
                        setDialog({ kind: "create-site", clientRow: row });
                      }}
                    >
                      New site
                    </button>
                    <button
                      type="button"
                      className="builder__navbtn"
                      onClick={() => {
                        setFailure(null);
                        setDialog({ kind: "edit-client", row });
                      }}
                    >
                      Edit client
                    </button>
                    <button
                      type="button"
                      className="builder__cancel"
                      onClick={() => {
                        setFailure(null);
                        setDialog({ kind: "archive-client", row });
                      }}
                    >
                      Archive client
                    </button>
                  </span>
                ) : null}
              </div>
              {clientSites.length === 0 ? (
                <p className="surface__state setup__empty-site" role="status">
                  No sites under this client yet.
                  {canManage && row.active
                    ? " Click New site above to add a campus or building (routes and schedules need a site, not just a client name)."
                    : ""}
                </p>
              ) : (
                <ul className="ckedit__rows">
                  {clientSites.map((site) => (
                    <li
                      key={site.siteId}
                      className={`ckedit__row${activeSiteId === site.siteId ? " ckedit__row--editing" : ""}`}
                    >
                      <span className="ckedit__name">{site.name}</span>
                      {site.active ? (
                        <span
                          className={`schedule__chip ${site.status === "live" ? "schedule__chip--credential" : "schedule__chip--partial"}`}
                        >
                          {site.status === "live" ? "Live" : "Setup"}
                        </span>
                      ) : (
                        <span className="schedule__chip schedule__chip--draft">Archived</span>
                      )}
                      <span className="ckedit__coords">
                        {[site.streetAddress, site.locality, site.region].filter(Boolean).join(", ") || "no address"}
                        {" - "}
                        {site.timezone}
                      </span>
                      {canManage && site.active ? (
                        <span className="ckedit__actions">
                          {site.status === "live" ? (
                            <button
                              type="button"
                              className="builder__navbtn"
                              onClick={() => {
                                setFailure(null);
                                setDialog({ kind: "lifecycle-site", site, next: "setup" });
                              }}
                            >
                              Return to setup
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="minter__confirm-go"
                              onClick={() => {
                                setFailure(null);
                                setDialog({ kind: "lifecycle-site", site, next: "live" });
                              }}
                            >
                              Take live
                            </button>
                          )}
                          <button
                            type="button"
                            className="builder__navbtn"
                            onClick={() => {
                              setFailure(null);
                              setDialog({ kind: "edit-site", site });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="builder__cancel"
                            onClick={() => {
                              setFailure(null);
                              setDialog({ kind: "archive-site", site });
                            }}
                          >
                            Archive
                          </button>
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })
      )}

      {canManage && dialog !== null && isDrawerDialog(dialog) ? (
        <GlassDrawer
          open
          busy={busy}
          dirty={formDirty}
          ariaLabel={dialogAriaLabel(dialog)}
          size={dialog.kind === "create-site" || dialog.kind === "edit-site" ? "lg" : "md"}
          onClose={clearDrawer}
        >
          {dialog.kind === "create-client" || dialog.kind === "edit-client" ? (
            <ClientForm
              key={dialog.kind === "edit-client" ? dialog.row.clientId : "create-client"}
              editing={dialog.kind === "edit-client" ? dialog.row : null}
              busy={busy}
              failure={failure}
              onDirtyChange={setFormDirty}
              onClose={requestDrawerClose}
              onSubmit={(name, slug) =>
                void run(
                  () =>
                    dialog.kind === "create-client"
                      ? createClient(client, accountId, name, slug)
                      : updateClient(client, accountId, dialog.row.clientId, name, slug),
                  (outcome) => {
                    if (dialog.kind === "create-client" && outcome.clientId !== null) {
                      setDialog({
                        kind: "create-site",
                        clientRow: {
                          clientId: outcome.clientId,
                          name: name.trim(),
                          slug: null,
                          active: true,
                        },
                      });
                      return true;
                    }
                  },
                )
              }
            />
          ) : dialog.kind === "create-site" || dialog.kind === "edit-site" ? (
            <SiteForm
              key={dialog.kind === "edit-site" ? dialog.site.siteId : "create-site"}
              editing={dialog.kind === "edit-site" ? dialog.site : null}
              clientName={dialog.kind === "create-site" ? dialog.clientRow.name : null}
              busy={busy}
              failure={failure}
              mapTilerKey={mapTilerKey}
              onDirtyChange={setFormDirty}
              onClose={requestDrawerClose}
              onSubmit={(draft) =>
                void run(async () => {
                  if (!siteDraftHasRequiredAddress(draft)) {
                    return { ok: false as const, reason: SITE_ADDRESS_REQUIRED_MESSAGE };
                  }
                  const resolved = await resolveSiteDraftWithCenter(draft, {
                    ...(mapTilerKey !== undefined ? { mapTilerKey } : {}),
                    previous: dialog.kind === "edit-site" ? dialog.site : null,
                  });
                  if (
                    typeof mapTilerKey === "string" &&
                    mapTilerKey.length > 0 &&
                    (resolved.centerLat === null || resolved.centerLng === null)
                  ) {
                    return {
                      ok: false as const,
                      reason:
                        "Could not verify that address on the map. Use Look up address or check street, city, and state.",
                    };
                  }
                  return dialog.kind === "create-site"
                    ? createSite(client, accountId, dialog.clientRow.clientId, resolved)
                    : updateSite(
                        client,
                        accountId,
                        dialog.site.siteId,
                        dialog.site.clientId,
                        resolved,
                      );
                })
              }
            />
          ) : null}
        </GlassDrawer>
      ) : null}

      {canManage && dialog !== null && !isDrawerDialog(dialog) ? (
        <GlassModal
          open
          busy={busy}
          ariaLabel={dialogAriaLabel(dialog)}
          onClose={() => {
            if (!busy) setDialog(null);
          }}
        >
          {dialog.kind === "archive-client" ? (
            <ConfirmArchive
              title={`Archive client - ${dialog.row.name}`}
              warning="The client and its sites leave setup and pickers, but every site, route, checkpoint, and all history stays preserved. Archived clients cannot be edited."
              busy={busy}
              failure={failure}
              onClose={() => setDialog(null)}
              onConfirm={() => void run(() => archiveClient(client, accountId, dialog.row.clientId))}
            />
          ) : dialog.kind === "archive-site" ? (
            <ConfirmArchive
              title={`Archive site - ${dialog.site.name}`}
              warning="The site leaves setup and pickers. Its routes, checkpoints, and history stay preserved. Archived sites cannot be edited."
              busy={busy}
              failure={failure}
              onClose={() => setDialog(null)}
              onConfirm={() => void run(() => archiveSite(client, accountId, dialog.site.siteId))}
            />
          ) : dialog.kind === "lifecycle-site" ? (
            <div className="minter__confirm" aria-label={dialogAriaLabel(dialog)}>
              <h3 className="minter__confirm-title">
                {dialog.next === "live" ? "Take live" : "Return to setup"} - {dialog.site.name}
              </h3>
              <p className="minter__confirm-warning">
                {dialog.next === "live"
                  ? "Going live makes this site visible to its guards and starts missed-checkpoint alerts. Make sure its routes, checkpoints, and mounted tags are ready."
                  : "Setup sites are hidden from guards and fire no missed-checkpoint alerts. Use this while you finish configuring the site; published schedules and history are preserved."}
              </p>
              {failure !== null ? (
                <p className="surface__state surface__state--error" role="alert">{failure}</p>
              ) : null}
              <div className="minter__confirm-actions">
                <button type="button" className="builder__navbtn" disabled={busy} onClick={() => setDialog(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="minter__confirm-go"
                  disabled={busy}
                  onClick={() => void run(() => setSiteLifecycleStatus(client, accountId, dialog.site.siteId, dialog.next))}
                >
                  {dialog.next === "live" ? "Confirm go-live" : "Confirm return to setup"}
                </button>
              </div>
            </div>
          ) : null}
        </GlassModal>
      ) : null}
    </div>
  );
}

function ClientForm({
  editing,
  busy,
  failure,
  onClose,
  onSubmit,
  onDirtyChange,
}: {
  editing: ClientRow | null;
  busy: boolean;
  failure: string | null;
  onClose: () => void;
  onSubmit: (name: string, slug: string | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const initialName = editing?.name ?? "";
  const [name, setName] = useState(initialName);
  const dirty = name !== initialName;
  // The action row renders on the drawer's panel edge, outside this form's DOM subtree,
  // so its submit button carries `form=` to stay bound to this form.
  const formId = useId();

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  return (
    <form
      id={formId}
      className="minter__confirm tpl__form"
      aria-label={editing === null ? "New client" : `Edit client - ${editing.name}`}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(name.trim(), null);
      }}
    >
      <h3 className="minter__confirm-title">{editing === null ? "New client" : `Edit client - ${editing.name}`}</h3>
      <p className="minter__confirm-note">
        A client is the organization or contract you protect (e.g. &quot;Louisville VA Health System&quot;).
        Physical locations are <strong>sites</strong> you add next under this client.
      </p>
      <label className="tpl__field">
        Client name
        <input
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Demo Hospital System"
          required
        />
      </label>
      {editing !== null ? (
        <p className="minter__confirm-note">
          Client ID: <code>{editing.clientId}</code> (stable UUID for integrations, not editable)
        </p>
      ) : null}
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
      <GlassDrawerFooter>
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="submit" form={formId} className="minter__confirm-go" disabled={busy}>
          {editing === null ? "Create client" : "Save client"}
        </button>
      </GlassDrawerFooter>
    </form>
  );
}

function SiteForm({
  editing,
  clientName,
  busy,
  failure,
  mapTilerKey,
  onClose,
  onSubmit,
  onDirtyChange,
}: {
  editing: ManagedSite | null;
  clientName: string | null;
  busy: boolean;
  failure: string | null;
  mapTilerKey?: string | undefined;
  onClose: () => void;
  onSubmit: (draft: SiteDraft) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const initialName = editing?.name ?? "";
  const initialStreet = editing?.streetAddress ?? "";
  const initialLocality = editing?.locality ?? "";
  const initialRegion = editing?.region ?? "";
  const initialPostal = editing?.postalCode ?? "";
  const initialTimezone = editing?.timezone ?? DEFAULT_SITE_TIMEZONE;
  const [name, setName] = useState(initialName);
  const [street, setStreet] = useState(initialStreet);
  const [locality, setLocality] = useState(initialLocality);
  const [region, setRegion] = useState(initialRegion);
  const [postal, setPostal] = useState(initialPostal);
  const [timezone, setTimezone] = useState(initialTimezone);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupMessage, setLookupMessage] = useState<string | null>(null);
  const [lookupOk, setLookupOk] = useState(false);
  // The action row renders on the drawer's panel edge, outside this form's DOM subtree,
  // so its submit button carries `form=` to stay bound to this form.
  const formId = useId();

  const dirty =
    name !== initialName ||
    street !== initialStreet ||
    locality !== initialLocality ||
    region !== initialRegion ||
    postal !== initialPostal ||
    timezone !== initialTimezone;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  function draftFromFields(): SiteDraft {
    const normalized = normalizeSiteAddressFields({
      streetAddress: street.trim() === "" ? null : street.trim(),
      locality: locality.trim() === "" ? null : locality.trim(),
      region: region.trim() === "" ? null : region.trim(),
      postalCode: postal.trim() === "" ? null : postal.trim(),
    });
    return {
      name: name.trim(),
      ...normalized,
      timezone: timezone.trim(),
    };
  }

  function applyNormalizedAddress(address: {
    streetAddress: string | null;
    locality: string | null;
    region: string | null;
    postalCode: string | null;
  }): void {
    if (address.streetAddress !== null) {
      setStreet(address.streetAddress);
    }
    if (address.locality !== null) {
      setLocality(address.locality);
    }
    if (address.region !== null) {
      setRegion(address.region);
    }
    if (address.postalCode !== null) {
      setPostal(address.postalCode);
    }
  }

  async function lookupAddress(): Promise<void> {
    if (lookupBusy) return;
    const draft = draftFromFields();
    if (!siteDraftHasRequiredAddress(draft)) {
      setLookupOk(false);
      setLookupMessage(SITE_ADDRESS_REQUIRED_MESSAGE);
      return;
    }
    setLookupBusy(true);
    setLookupMessage(null);
    setLookupOk(false);
    const outcome = await geocodeAddress(formatGeocodeQuery(draft), mapTilerKey);
    if (outcome.ok) {
      if (outcome.result.normalizedAddress !== null) {
        applyNormalizedAddress(outcome.result.normalizedAddress);
      } else {
        applyNormalizedAddress(normalizeSiteAddressFields(draft));
      }
      setLookupOk(true);
      setLookupMessage(
        `Address verified at ${outcome.result.lat.toFixed(5)}, ${outcome.result.lng.toFixed(5)}. Normalized fields updated; map center saves when you create the site.`,
      );
    } else {
      setLookupMessage(outcome.reason);
    }
    setLookupBusy(false);
  }

  return (
    <form
      id={formId}
      className="minter__confirm tpl__form"
      aria-label={editing === null ? `New site - ${clientName ?? ""}` : `Edit site - ${editing.name}`}
      onSubmit={(event) => {
        event.preventDefault();
        const draft = draftFromFields();
        if (!siteDraftHasRequiredAddress(draft)) {
          setLookupOk(false);
          setLookupMessage(SITE_ADDRESS_REQUIRED_MESSAGE);
          return;
        }
        onSubmit(draft);
      }}
    >
      <h3 className="minter__confirm-title">
        {editing === null ? `New site under ${clientName ?? "client"}` : `Edit site - ${editing.name}`}
      </h3>
      <p className="minter__confirm-note">
        A site is a physical property. Street, city, and state are required so we can center the
        property map and Command Center on the right location. Routes attach to sites.
      </p>
      <label className="tpl__field">
        Site name
        <input
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. North Campus"
          required
        />
      </label>
      <fieldset className="tpl__fieldset">
        <legend className="tpl__legend">Property address</legend>
        <label className="tpl__field">
          Street address
          <input
            type="text"
            value={street}
            onChange={(event) => {
              setStreet(event.target.value);
              setLookupMessage(null);
              setLookupOk(false);
            }}
            placeholder="e.g. 100 Main Street"
            required
          />
        </label>
        <div className="tpl__field-row">
          <label className="tpl__field">
            City
            <input
              type="text"
              value={locality}
              onChange={(event) => {
                setLocality(event.target.value);
                setLookupMessage(null);
                setLookupOk(false);
              }}
              required
            />
          </label>
          <label className="tpl__field">
            State/Region
            <input
              type="text"
              value={region}
              onChange={(event) => {
                setRegion(event.target.value);
                setLookupMessage(null);
                setLookupOk(false);
              }}
              required
            />
          </label>
          <label className="tpl__field">
            Postal code (optional)
            <input type="text" value={postal} onChange={(event) => setPostal(event.target.value)} />
          </label>
        </div>
        <div className="setup__lookup-actions">
          <button
            type="button"
            className="builder__navbtn"
            disabled={busy || lookupBusy}
            onClick={() => void lookupAddress()}
          >
            {lookupBusy ? "Looking up..." : "Look up address"}
          </button>
        </div>
        {lookupMessage !== null ? (
          <p
            className={lookupOk ? "setup__lookup-status" : "setup__lookup-status setup__lookup-status--error"}
            role={lookupOk ? "status" : "alert"}
          >
            {lookupMessage}
          </p>
        ) : null}
      </fieldset>
      <label className="tpl__field">
        Timezone (drives scheduling for this site)
        <TimezoneSelect value={timezone} onChange={setTimezone} required />
      </label>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
      <GlassDrawerFooter>
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="submit" form={formId} className="minter__confirm-go" disabled={busy}>
          {editing === null ? "Create site" : "Save site"}
        </button>
      </GlassDrawerFooter>
    </form>
  );
}

function ConfirmArchive({
  title,
  warning,
  busy,
  failure,
  onClose,
  onConfirm,
}: {
  title: string;
  warning: string;
  busy: boolean;
  failure: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="minter__confirm" aria-label={title}>
      <h3 className="minter__confirm-title">{title}</h3>
      <p className="minter__confirm-warning">{warning}</p>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="minter__confirm-go" onClick={onConfirm} disabled={busy}>
          Confirm archive
        </button>
      </div>
    </div>
  );
}
